import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';

import AuthShell from './_AuthShell';
import { Alert } from "../../components/DCKeeper";
import { passwordApi } from "../../helpers/dck_helper";

/**
 * 忘記密碼(/auth-recoverpw)。
 *
 * 版面用模板 auth-recoverpw 原樣(Figma DCK-00 29:3119 是這頁的截圖換中文文案)。
 * 走真後端契約(ticket 09):進頁 GET PasswordRecovery 取模型(display_captcha),
 * 送出 POST PasswordRecoverySend。畫面講 `result`(2026-09-04 實測:
 * 沒註冊＝「未找到電子信箱」;有註冊才真的寄)。失敗(5xx／網路)不假裝已寄出。
 *
 * ⚠️ display_captcha=true 時的驗證碼機制文件未載(元件/圖形來源皆未定義),
 * 依 CLAUDE.md「未確認不動工」:先擋提交並說明,待後端確認後補。
 */
/** 2026-09-04 實測：沒註冊只回這一句。不猜「不存在／查無」等未量過的句。 */
const EMAIL_NOT_FOUND = "未找到電子信箱";

const SENT_FALLBACK = "若該 Email 已註冊，重設連結已寄出（30 分鐘內有效），請至信箱查收。";

const RecoverPassword = () => {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [sentMessage, setSentMessage] = useState(SENT_FALLBACK);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [captchaRequired, setCaptchaRequired] = useState(false);

    // 取模型:目前只用 display_captcha;模型取不到不擋流程(送出時後端仍會把關)
    useEffect(() => {
        let alive = true;
        passwordApi.getRecoveryModel()
            .then((m) => { if (alive) setCaptchaRequired(!!(m && m.display_captcha)); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const submit = (e) => {
        e.preventDefault();
        if (!email.trim() || captchaRequired) return;
        setBusy(true);
        setError("");
        passwordApi.recoverySend({ email: email.trim() })
            .then((res) => {
                const result = res && res.result != null ? String(res.result) : "";
                if (result === EMAIL_NOT_FOUND) {
                    setError(result);
                    return;
                }
                if (!result.trim()) {
                    console.warn(
                        "[auth] PasswordRecoverySend 回了 200 但 result 是空的。",
                        "畫面改用預設已寄出文案；若後端其實沒寄，請查 SMTP。"
                    );
                    setSentMessage(SENT_FALLBACK);
                    setSent(true);
                    return;
                }
                setSentMessage(result);
                setSent(true);
            })
            .catch((err) => {
                setError(typeof err === "string" ? err : "寄送失敗，請稍後再試");
            })
            .finally(() => { setBusy(false); });
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>重設密碼 | DC Keeper</title>
            </Helmet>
            <AuthShell title="重新設定密碼" subtitle="透過 DC Keeper 重設您的密碼">
                {sent ? (
                    <div className="alert alert-success text-center my-4 font-size-12" role="alert">
                        {sentMessage}
                    </div>
                ) : (
                    <>
                        <div className="alert alert-success text-center my-4 font-size-12" role="alert">
                            請輸入您的 Email，系統將寄送重設說明給您！
                        </div>
                        {captchaRequired ? (
                            <Alert color="warning" className="my-4 font-size-12">
                                此操作需要驗證碼，目前尚未支援，請聯繫系統管理者協助重設密碼。
                            </Alert>
                        ) : null}
                        {error ? (
                            <Alert color="danger" className="my-4 font-size-12">{error}</Alert>
                        ) : null}
                        <form className="mt-4 pt-2" onSubmit={submit}>
                            <div className="form-floating form-floating-custom mb-4">
                                <input type="email" className="form-control" id="input-email" required
                                    placeholder="請輸入 Email" value={email}
                                    onChange={(e) => setEmail(e.target.value)} />
                                <label htmlFor="input-email">Email</label>
                                <div className="form-floating-icon">
                                    <i className="bx bx-mail-send" />
                                </div>
                            </div>
                            <div className="mb-3">
                                <button className="btn btn-primary w-100 waves-effect waves-light"
                                    type="submit" disabled={busy || captchaRequired}>寄送重設連結</button>
                            </div>
                        </form>
                    </>
                )}

                <div className="mt-4 pt-3 text-center">
                    <p className="text-muted mb-0">
                        想起密碼了？<Link to="/auth-login" className="text-primary fw-semibold"> 直接登入 </Link>
                    </p>
                </div>
            </AuthShell>
        </React.Fragment>
    );
};

export default RecoverPassword;
